import React, { useState, useEffect, useRef } from 'react'
import HubBadge from 'components/sidebar/hubBadge'
import { useSearchParams } from 'react-router-dom'
import Button from 'components/button'
import CreateModal from 'views/home/components/createModal'
import api from 'helpers/api'
import { useApplicationStore } from 'hooks/application'
import { IProject } from 'types'
import { useAuth } from 'hooks/auth'
import { MFA, Plus, FilledStar, NonFilledStar, Search, Projects, List as ListIcon, Globe } from 'components/icons'
import SortMenu, { SortSection } from 'components/sortMenu'
import { PROJECT } from 'app-constants'
import { capitaliseFirstLetter } from 'helpers/utils'
import { formatShortDate } from 'helpers/date'
import { isFavourite, toggleFavouriteHub } from 'helpers/favourites'
import { loadViewMode, saveViewMode, ViewMode } from 'helpers/viewMode'
import { HUB_BASE } from 'routes'
import Skeleton from 'components/skeleton'
import Input from 'components/input'
import useFocusFromParam from 'hooks/useFocusFromParam'
import Tooltip from 'components/tooltip'
import { useHubCreation } from 'hooks/useHubCreation'
import { Table, ITableData, SortColumn } from 'components/table'
import { useHubsData } from 'providers/hubs'
import { COMMUNITY_PROJECT_ID, VIEW_MODE_KEY, pinnedHubs, PinnedHub } from 'views/home/constants'

type Scope =
  | { kind: 'all' }
  | { kind: 'favs' }
  | { kind: 'community' }
  | { kind: 'archived' }
  | { kind: 'unassigned' }
  | { kind: 'site'; id: string }

type SortBy = 'name' | 'createdAt' | 'favourite'
interface SortState {
  by: SortBy
  direction: 'asc' | 'desc'
}
const DEFAULT_SORT: SortState = { by: 'favourite', direction: 'desc' }

const HUB_SORT_SECTIONS: SortSection<SortBy>[] = [
  {
    label: 'Favourites',
    options: [
      { by: 'favourite', direction: 'desc', label: 'Starred first' },
      { by: 'favourite', direction: 'asc', label: 'Starred last' }
    ]
  },
  {
    label: 'Name',
    options: [
      { by: 'name', direction: 'asc', label: 'A → Z' },
      { by: 'name', direction: 'desc', label: 'Z → A' }
    ]
  },
  {
    label: 'Date created',
    options: [
      { by: 'createdAt', direction: 'desc', label: 'Newest first' },
      { by: 'createdAt', direction: 'asc', label: 'Oldest first' }
    ]
  }
]

const scopeFromParams = (searchParams: URLSearchParams): Scope => {
  const scope = searchParams.get('scope') || 'all'
  if (scope === 'favs') return { kind: 'favs' }
  if (scope === 'community') return { kind: 'community' }
  if (scope === 'archived') return { kind: 'archived' }
  if (scope === 'unassigned') return { kind: 'unassigned' }
  if (scope === 'site') {
    const id = searchParams.get('siteId')
    if (id) return { kind: 'site', id }
  }
  return { kind: 'all' }
}

interface HubTileProps {
  project: IProject
  size: number
}

// The hub's badge, as the sidebar shows it.
const HubTile: React.FC<HubTileProps> = ({ project, size }) => (
  <HubBadge name={project.name} colour={project.primaryColour} textColour={project.fontColour} size={size} />
)

interface HubCardProps {
  project: IProject
  favourite: boolean
  onFavourite: (id: string) => void
}

const HubCard: React.FC<HubCardProps> = ({ project, favourite, onFavourite }) => {
  const background = project.isDeleted ? 'bg-surface-strong' : 'bg-surface'
  return (
    <a
      className={`group flex items-center p-3 rounded-lg cursor-pointer transition-all border border-divider box-border h-[93px] hover:shadow-heavy hover:-translate-x-px hover:-translate-y-px ${background}`}
      href={`${HUB_BASE}${project.publicId}`}
      data-cy={project.name}
    >
      <HubTile project={project} size={56} />
      <div className="flex-1 min-w-0 px-3">
        <div className="flex items-center gap-1.5 min-w-0">
          <Tooltip content={project.name} showOnlyIfTruncated delay={300}>
            <div className="text-body text-base font-medium truncate">
              {project.name}
              {project.isDeleted ? ' (Archived)' : ''}
            </div>
          </Tooltip>
          {project.mfaRequired && (
            <Tooltip content="MFA required">
              <span className="flex items-center text-muted shrink-0">
                <MFA className="w-[16px] h-[16px]" />
              </span>
            </Tooltip>
          )}
          {project.public && (
            <Tooltip content="Public hub">
              <span className="flex items-center text-muted shrink-0">
                <Globe className="w-[14px] h-[14px]" />
              </span>
            </Tooltip>
          )}
        </div>
        {project.site && <div className="text-sm text-muted truncate mt-1">{project.site.name}</div>}
      </div>
      <div
        role="button"
        tabIndex={0}
        aria-label={favourite ? 'Remove from favourites' : 'Add to favourites'}
        className={`p-1 cursor-pointer transition-opacity ${favourite ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
        onClick={(event) => {
          event.preventDefault()
          event.stopPropagation()
          onFavourite(project.publicId)
        }}
      >
        {favourite ? <FilledStar className="h-[20px] w-[20px]" /> : <NonFilledStar className="h-[20px] w-[20px]" />}
      </div>
    </a>
  )
}

const HUB_LIST_HEADERS = [
  { header: 'Name', id: 'name' },
  { header: 'Organisation', id: 'organisation' },
  { header: 'Site', id: 'site' },
  { header: 'Created', id: 'createdAt' },
  {
    header: 'Favourite',
    id: 'favourite'
  }
]

const HUB_LIST_DEFAULT_SORT: SortColumn[] = [
  { id: 'favourite', ascending: false },
  { id: 'name', ascending: true }
]

const COMMUNITY_LIST_HEADERS = [
  { header: 'Name', id: 'name' },
  { header: 'Type', id: 'type' }
]

const buildHubRows = (
  projects: IProject[],
  favourites: Partial<IProject>[] | undefined,
  onFavourite: (id: string) => void
): Record<string, ITableData>[] =>
  projects.map((project) => {
    const fav = isFavourite(project.publicId, favourites)
    return {
      name: {
        label: (
          <a href={`${HUB_BASE}${project.publicId}`} data-cy={project.name} className="flex items-center gap-3 min-w-0">
            <HubTile project={project} size={28} />
            <Tooltip content={project.name} showOnlyIfTruncated delay={300}>
              <span className="text-sm text-body truncate font-medium">
                {project.name}
                {project.isDeleted ? ' (Archived)' : ''}
              </span>
            </Tooltip>
            {project.mfaRequired && (
              <Tooltip content="MFA required">
                <span className="flex items-center text-muted shrink-0">
                  <MFA className="w-[16px] h-[16px]" />
                </span>
              </Tooltip>
            )}
            {project.public && (
              <Tooltip content="Public hub">
                <span className="flex items-center text-muted shrink-0">
                  <Globe className="w-[14px] h-[14px]" />
                </span>
              </Tooltip>
            )}
          </a>
        ),
        value: project.name
      },
      organisation: {
        label: project.site?.organisation?.name || '—',
        value: project.site?.organisation?.name || ''
      },
      site: { label: project.site?.name || '—', value: project.site?.name || '' },
      createdAt: { label: formatShortDate(project.createdAt), value: project.createdAt },
      favourite: {
        label: (
          <div
            role="button"
            tabIndex={0}
            aria-label={fav ? 'Remove from favourites' : 'Add to favourites'}
            className="cursor-pointer"
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
              onFavourite(project.publicId)
            }}
          >
            {fav ? <FilledStar className="h-[18px] w-[18px]" /> : <NonFilledStar className="h-[18px] w-[18px]" />}
          </div>
        ),
        value: fav ? 1 : 0
      }
    }
  })

const buildCommunityRows = (hubs: PinnedHub[]): Record<string, ITableData>[] =>
  hubs.map((hub) => ({
    name: {
      label: (
        <a href={`${HUB_BASE}${hub.publicId}`} className="flex items-center gap-3 min-w-0">
          <HubBadge name={hub.name} initials={hub.abbreviation} colour={hub.backgroundColor} size={28} />
          <Tooltip content={hub.name}>
            <span className="text-sm text-body truncate font-medium">{hub.name}</span>
          </Tooltip>
        </a>
      ),
      value: hub.name
    },
    type: { label: 'Public template · Morta', value: 'Public template · Morta' }
  }))

const Home: React.FC = () => {
  const [search, setSearch] = useState<string>('')
  // The sidebar's per-site search lands here with focus=search, ready to type.
  const searchInput = useRef<HTMLInputElement>(null)
  useFocusFromParam(searchInput, 'search')
  const [viewMode, setViewModeState] = useState<ViewMode>(() => loadViewMode(VIEW_MODE_KEY))
  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT)

  const { projects, loading: projectsLoading, reload } = useHubsData()
  const { canCreateHub, restrictionMessage } = useHubCreation()
  const { displayErrorMessage, sidebarWidth } = useApplicationStore()
  const { user, updateUser } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()

  const scope = scopeFromParams(searchParams)

  const setViewMode = (mode: ViewMode) => {
    setViewModeState(mode)
    saveViewMode(VIEW_MODE_KEY, mode)
  }

  useEffect(() => {
    const getFile = async (url: string, name: string | null) => {
      let signedFileTokenResponse
      try {
        signedFileTokenResponse = await api.signUrl(url)
      } catch (e) {
        displayErrorMessage(e)
        return
      }
      const downloadUrl = signedFileTokenResponse.data.url

      const a = document.createElement('a')
      a.style.display = 'none'
      a.href = downloadUrl
      const filename = signedFileTokenResponse.data.filename || 'downloaded_file'
      a.download = name ? name : filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      const next = new URLSearchParams(searchParams)
      next.delete('fileUrl')
      next.delete('fileName')
      next.delete('tableId')
      next.delete('columnId')
      setSearchParams(next, { replace: true })
    }

    const getTableFile = async (tableId: string, columnId: string, filename: string) => {
      api
        .getFile({
          tableId: tableId,
          columnId: columnId,
          filename: filename
        })
        .catch((error) => {
          displayErrorMessage(error)
        })
    }

    const fileUrl = searchParams.get('fileUrl')
    const fileName = searchParams.get('fileName')
    const tableId = searchParams.get('tableId')
    const columnId = searchParams.get('columnId')
    if (fileUrl) {
      try {
        getFile(fileUrl, fileName)
      } catch (e) {
        displayErrorMessage(e)
      }
    } else if (fileName && tableId && columnId) {
      try {
        getTableFile(tableId, columnId, fileName)
      } catch (e) {
        displayErrorMessage(e)
      }
    }
  }, [searchParams])

  useEffect(() => {
    document.title = 'Morta | Home'
  }, [])

  // The same toggle as the sidebar's star, so the two can't drift apart.
  const favouriteProject = (projectId: string) => {
    if (!user) return
    toggleFavouriteHub({
      user,
      projectId,
      updateUser,
      request: (args) => api(args),
      onError: displayErrorMessage
    })
  }

  const ownProjects = projects.filter((p) => p.publicId !== COMMUNITY_PROJECT_ID)

  const scopedHubs = (() => {
    let base: IProject[] = ownProjects
    if (scope.kind === 'favs') {
      base = ownProjects.filter((p) => isFavourite(p.publicId, user?.favourites))
    } else if (scope.kind === 'site') {
      base = ownProjects.filter((p) => p.site?.publicId === scope.id)
    } else if (scope.kind === 'unassigned') {
      base = ownProjects.filter((p) => !p.site || !p.site.publicId)
    } else if (scope.kind === 'archived') {
      base = ownProjects.filter((p) => p.isDeleted)
    }
    if (scope.kind !== 'archived') {
      base = base.filter((p) => !p.isDeleted)
    }
    const filtered = search ? base.filter((p) => p.name.toLowerCase().includes(search.toLowerCase())) : base
    const direction = sort.direction === 'asc' ? 1 : -1
    const favScore = (p: IProject) => (isFavourite(p.publicId, user?.favourites) ? 1 : 0)
    const compareName = (a: IProject, b: IProject) => a.name.toLowerCase().localeCompare(b.name.toLowerCase())
    return filtered.slice().sort((a, b) => {
      let primary = 0
      if (sort.by === 'createdAt') {
        primary = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      } else if (sort.by === 'favourite') {
        primary = favScore(a) - favScore(b)
      } else {
        primary = compareName(a, b)
      }
      primary *= direction
      if (primary !== 0) return primary
      return sort.by === 'name' ? 0 : compareName(a, b)
    })
  })()

  const scopeLabel = (() => {
    if (scope.kind === 'all') return 'All hubs'
    if (scope.kind === 'favs') return 'Starred'
    if (scope.kind === 'community') return 'Community'
    if (scope.kind === 'archived') return 'Archived'
    if (scope.kind === 'unassigned') return 'Other'
    const site = ownProjects.map((p) => p.site).find((s) => s?.publicId === scope.id)
    return site?.name || 'Site'
  })()

  const toolbarIconBtn =
    'flex items-center justify-center w-11 h-[38px] transition-colors text-muted hover:bg-surface-sunken'
  const toolbarIconBtnActive =
    'flex items-center justify-center w-11 h-[38px] bg-really-light-blue text-blue hover:bg-really-light-blue'

  const showMainContent = !projectsLoading

  const newHubButton = (
    <Button
      internalType="accept"
      data-tour="new-hub"
      className="h-9 px-3 text-sm gap-1.5 max-lg:hidden"
      disabled={!canCreateHub}
      onClick={() => setModalOpen(true)}
    >
      <Plus className="mr-1.5" />
      New {capitaliseFirstLetter(PROJECT)}
    </Button>
  )

  return (
    <div className="w-full h-full overflow-y-auto">
      <div className="px-8 py-6 max-w-[1400px] max-lg:px-5">
        <h1 className={`text-2xl font-bold text-body mb-5 ${sidebarWidth === 0 ? 'ml-9' : ''}`}>
          {scopeLabel} <span className="text-faint font-normal text-lg">({scopedHubs.length})</span>
        </h1>

        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <div className="relative h-9 w-[380px] max-lg:w-full">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none">
              <Search />
            </div>
            <Input
              ref={searchInput}
              className="h-9 pl-9 pr-3"
              placeholder={`Search in ${scopeLabel.toLowerCase()}…`}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>

          {canCreateHub ? (
            newHubButton
          ) : (
            // A disabled button gets no hover events, so the wrapper carries the tooltip
            <Tooltip content={restrictionMessage}>
              <span className="inline-flex max-lg:hidden" data-testid="new-hub-restricted">
                {newHubButton}
              </span>
            </Tooltip>
          )}

          <div className="ml-auto flex items-center gap-2">
            {viewMode === 'grid' && <SortMenu value={sort} onChange={setSort} sections={HUB_SORT_SECTIONS} />}
            <div className="h-10 flex items-center border border-divider rounded-lg bg-surface overflow-hidden">
              <Button
                internalType="transparent"
                onClick={() => setViewMode('grid')}
                aria-label="Grid view"
                className={viewMode === 'grid' ? toolbarIconBtnActive : toolbarIconBtn}
              >
                <Projects width={16} height={16} />
              </Button>
              <Button
                internalType="transparent"
                onClick={() => setViewMode('list')}
                aria-label="List view"
                className={viewMode === 'list' ? toolbarIconBtnActive : toolbarIconBtn}
              >
                <ListIcon width={16} height={16} />
              </Button>
            </div>
          </div>
        </div>

        <div data-tour="hub-list">
          {projectsLoading && (
            <div className="grid grid-cols-3 max-lg:grid-cols-1 gap-4">
              <Skeleton className="h-[93px]" />
              <Skeleton className="h-[93px]" />
              <Skeleton className="h-[93px]" />
            </div>
          )}

          {showMainContent && scope.kind === 'community' && viewMode === 'grid' && (
            <div className="grid grid-cols-3 max-lg:grid-cols-1 gap-4">
              {pinnedHubs.map((hub) => (
                <a
                  key={hub.publicId}
                  className="flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all border border-divider box-border bg-really-light-blue h-[93px] hover:shadow-heavy hover:-translate-x-px hover:-translate-y-px"
                  href={`${HUB_BASE}${hub.publicId}`}
                >
                  <HubBadge name={hub.name} initials={hub.abbreviation} colour={hub.backgroundColor} size={56} />
                  <div className="flex-1 min-w-0">
                    <Tooltip content={hub.name}>
                      <div className="text-base font-medium text-body truncate">{hub.name}</div>
                    </Tooltip>
                    <div className="text-sm text-muted mt-1">Public template · Morta</div>
                  </div>
                </a>
              ))}
            </div>
          )}

          {showMainContent && scope.kind === 'community' && viewMode === 'list' && (
            <Table
              data={buildCommunityRows(pinnedHubs)}
              include={COMMUNITY_LIST_HEADERS}
              defaultSort="name"
              defaultSortAscending={true}
            />
          )}

          {showMainContent && scope.kind !== 'community' && viewMode === 'grid' && (
            <div className="grid grid-cols-3 max-lg:grid-cols-1 gap-4">
              {scopedHubs.map((project) => (
                <HubCard
                  key={project.publicId}
                  project={project}
                  favourite={isFavourite(project.publicId, user?.favourites)}
                  onFavourite={favouriteProject}
                />
              ))}
            </div>
          )}

          {showMainContent && scope.kind !== 'community' && viewMode === 'list' && (
            <Table
              data={buildHubRows(scopedHubs, user?.favourites, favouriteProject)}
              include={HUB_LIST_HEADERS}
              defaultSort="name"
              defaultSortAscending={true}
              defaultSortColumns={HUB_LIST_DEFAULT_SORT}
            />
          )}

          {showMainContent && scope.kind !== 'community' && viewMode === 'grid' && scopedHubs.length === 0 && (
            <div className="text-center py-20 text-muted">Nothing here yet.</div>
          )}
        </div>
      </div>

      {modalOpen && <CreateModal id="create-hub-modal" open={modalOpen} setOpen={setModalOpen} onCreate={reload} />}
    </div>
  )
}

export default Home
